import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Plus, Image as ImageIcon, Scissors, Tag, ArrowUpDown, X as XIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fehlerText, listSequences, deleteSequence, API } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { toast } from "sonner";
import HelpButton from "@/components/HelpButton";
import HoverPreview from "@/components/HoverPreview";

const FORMAT_CHIPS = ["Outdoor", "Beach", "Indoor"];
const DIVISION_CHIPS = ["Open", "Women", "Mixed"];
const STATUS_CHIPS = ["draft", "uploaded", "calibrated", "ready"];
const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "most-sequences", label: "Most sequences" },
  { value: "most-labeled", label: "Most labeled" },
  { value: "name-az", label: "Name A → Z" },
];

export default function Library() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const [fmt, setFmt] = useState(null);    // null = all
  const [div, setDiv] = useState(null);
  const [stat, setStat] = useState(null);
  const [sort, setSort] = useState("newest");

  const load = async () => {
    setLoading(true);
    try {
      setItems(await listSequences());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (e, id) => {
    e.stopPropagation();
    if (!confirm("Delete this match and all its sequences?")) return;
    setItems((prev) => prev.filter((s) => s.id !== id));
    try {
      await deleteSequence(id);
      toast.success("Match deleted");
    } catch (err) {
      const msg = fehlerText(err) || err?.message || "Delete failed";
      toast.error(`Could not delete: ${msg}`);
    } finally {
      load();
    }
  };

  const clearFilters = () => {
    setFmt(null); setDiv(null); setStat(null); setQ("");
  };

  const filtered = useMemo(() => {
    let out = items;
    if (q) {
      const ql = q.toLowerCase();
      out = out.filter((s) =>
        s.name.toLowerCase().includes(ql) || s.id.startsWith(q)
      );
    }
    if (fmt) out = out.filter((s) => s.format === fmt);
    if (div) out = out.filter((s) => s.gender === div);
    if (stat) out = out.filter((s) => s.status === stat);

    const sorters = {
      newest: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
      oldest: (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
      "most-sequences": (a, b) => (b.num_clips ?? 0) - (a.num_clips ?? 0),
      "most-labeled": (a, b) => (b.num_labeled ?? 0) - (a.num_labeled ?? 0),
      "name-az": (a, b) => a.name.localeCompare(b.name),
    };
    return [...out].sort(sorters[sort] || sorters.newest);
  }, [items, q, fmt, div, stat, sort]);

  const filterActive = !!(fmt || div || stat || q);

  return (
    <div className="min-h-screen" data-testid="library-page">
      <header className="px-8 pt-8 pb-6 border-b border-border">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              Database
            </div>
            <h1 className="font-display font-black uppercase text-4xl tracking-tight">
              Match Library
            </h1>
            <div className="mt-2 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
              <span className="text-foreground">{filtered.length}</span>
              {filterActive && <> / <span className="text-foreground">{items.length}</span></>} matches
            </div>
          </div>
          <div className="flex gap-3 items-center">
            <Input
              data-testid="library-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or ID…"
              className="w-72 bg-secondary border-border focus:border-primary"
            />
            <Button
              data-testid="library-new-btn"
              onClick={() => navigate("/admin/wizard")}
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-display font-bold uppercase tracking-widest"
            >
              <Plus className="w-4 h-4 mr-2" /> New
            </Button>
            <HelpButton topic="library" />
          </div>
        </div>

        {/* Filter row */}
        <div className="mt-5 flex items-center gap-4 flex-wrap">
          <ChipGroup label="Format" testId="filter-format" options={FORMAT_CHIPS} value={fmt} onChange={setFmt} />
          <span className="w-px h-5 bg-border" />
          <ChipGroup label="Division" testId="filter-division" options={DIVISION_CHIPS} value={div} onChange={setDiv} />
          <span className="w-px h-5 bg-border" />
          <ChipGroup label="Status" testId="filter-status" options={STATUS_CHIPS} value={stat} onChange={setStat} />

          <div className="ml-auto flex items-center gap-2">
            <ArrowUpDown className="w-3.5 h-3.5 text-muted-foreground" />
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="library-sort" className="w-48 h-8 bg-secondary border-border font-mono text-[11px] uppercase tracking-widest">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} className="font-mono text-xs uppercase tracking-widest">
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {filterActive && (
              <Button
                data-testid="library-clear-filters"
                size="sm" variant="ghost"
                onClick={clearFilters}
                className="h-8 px-2 hover:bg-accent hover:text-primary font-mono text-[10px] uppercase tracking-widest"
              >
                <XIcon className="w-3 h-3 mr-1" /> Clear
              </Button>
            )}
          </div>
        </div>
      </header>

      <div className="px-8 py-6">
        {loading ? (
          <div className="text-center text-muted-foreground py-20 font-mono text-xs uppercase tracking-widest">
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-muted-foreground py-20" data-testid={TID.libraryTable}>
            {items.length === 0
              ? "No matches yet — click NEW to upload one."
              : "No matches for those filters."}
          </div>
        ) : (
          <div
            data-testid={TID.libraryTable}
            className="grid gap-5 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4"
          >
            {filtered.map((s) => (
              <MatchCard key={s.id} match={s} onOpen={() => navigate(`/admin/wizard/${s.id}`)} onDelete={(e) => handleDelete(e, s.id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ChipGroup({ label, options, value, onChange, testId }) {
  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
      <div className="flex items-center gap-1">
        {options.map((opt) => {
          const active = value === opt;
          return (
            <button
              key={opt}
              data-testid={`${testId}-${opt}`}
              onClick={() => onChange(active ? null : opt)}
              className={`px-2.5 py-1 rounded-sm border font-mono text-[10px] uppercase tracking-widest transition-colors ${
                active
                  ? "bg-primary text-primary-foreground border-primary"
                  : "bg-secondary text-muted-foreground border-border hover:text-foreground hover:border-primary/40"
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MatchCard({ match, onOpen, onDelete }) {
  // Use the API preview endpoint as fallback if presigned thumbnail_url expired.
  const [src, setSrc] = useState(match.thumbnail_url || `${API}/sequences/${match.id}/preview`);
  const [failed, setFailed] = useState(false);
  // Lazy-mount the looped annotated MP4 preview only when the user
  // actually hovers the card. Without this gate the Library would spin
  // up N parallel MP4 streams on render and torch B2 egress.
  const [hovering, setHovering] = useState(false);

  return (
    <div
      data-testid={`match-card-${match.id}`}
      onClick={onOpen}
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      className="group border border-border bg-card rounded-sm overflow-hidden hover:border-primary/60 transition-colors cursor-pointer flex flex-col"
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-secondary overflow-hidden">
        {failed ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-muted-foreground">
            <ImageIcon className="w-6 h-6 mb-1" />
            <span className="font-mono text-[10px] uppercase tracking-widest">No preview</span>
          </div>
        ) : (
          <img
            src={src}
            alt={match.name}
            loading="lazy"
            onError={() => {
              // Try the on-demand backfill endpoint once.
              if (src !== `${API}/sequences/${match.id}/preview`) {
                setSrc(`${API}/sequences/${match.id}/preview`);
              } else {
                setFailed(true);
              }
            }}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
          />
        )}
        {/* Annotated-MP4 hover-preview overlays the static thumbnail —
            only mounted while the user hovers AND the backend has
            flagged a tracked clip for this match. Cross-fades in via
            absolute positioning so we don't fight the existing image
            layout. */}
        {hovering && match.preview_clip_id && (
          <div data-testid={`match-card-preview-${match.id}`}
               className="absolute inset-0 animate-in fade-in duration-200">
            <HoverPreview
              clipId={match.preview_clip_id}
              className="w-full h-full"
              fallbackLabel="render queued"
            />
          </div>
        )}
        <div className="absolute top-2 right-2">
          <Badge className="bg-card/90 backdrop-blur text-foreground border border-border font-mono text-[9px] uppercase tracking-widest">
            {match.status}
          </Badge>
        </div>
        {typeof match.duration_s === "number" && (
          <div className="absolute bottom-2 right-2 px-1.5 py-0.5 bg-black/70 backdrop-blur font-mono text-[10px] text-white rounded-sm">
            {fmtDuration(match.duration_s)}
          </div>
        )}
      </div>

      {/* Body */}
      <div className="p-4 flex-1 flex flex-col">
        <div className="font-mono text-[10px] text-primary">#{match.id.slice(0, 8)}</div>
        <div className="font-medium text-sm mt-1 line-clamp-2 break-words" title={match.name}>{match.name}</div>

        <div className="mt-3 flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
          <span>{match.format || "—"}</span>
          <span>·</span>
          <span>{match.gender || "—"}</span>
        </div>

        <div className="mt-3 flex items-center gap-4 font-mono text-[10px] text-muted-foreground">
          <span className="flex items-center gap-1"><Scissors className="w-3 h-3 text-primary" /> <span className="text-foreground">{match.num_clips ?? 0}</span> seq</span>
          <span className="flex items-center gap-1"><Tag className="w-3 h-3 text-primary" /> <span className="text-foreground">{match.num_labeled ?? 0}</span> labeled</span>
        </div>

        <div className="mt-4 flex gap-2 pt-3 border-t border-border">
          <Button
            data-testid={`match-open-${match.id}`}
            size="sm"
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            className="flex-1 bg-primary/15 text-primary hover:bg-primary hover:text-primary-foreground border border-primary/40 font-display font-bold uppercase tracking-widest text-[10px]"
          >
            Open
          </Button>
          <Button
            data-testid={`match-delete-${match.id}`}
            size="sm"
            variant="ghost"
            onClick={onDelete}
            className="hover:bg-destructive/20 hover:text-destructive"
            title="Delete match"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function fmtDuration(s) {
  const m = Math.floor(s / 60);
  const sec = Math.round(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}
